import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { t } from 'i18next';
import SVGIcon from '../../atoms/icons';
import LanguageSelect from '../../molecules/language-select';
import Text from '../../atoms/text';
import { IUser } from '../../../services/User.service';
import { Link, useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import AuthService from '../../../services/Auth.service';
import { HomeLoaderData } from '../../../types/homeLoaderData';
import ClientService, { IClient } from '../../../services/Client.service';
import Select from '../../atoms/select';
import { EditableChangeEvent } from '../../../utils/types/editable';
import { NotificationToast } from '../notification-toast';

interface HeaderProps {
	onSidebarIconClick: () => void;
	idClienteSelecionado: string;
	onClienteChange: (idCliente: string) => void;
}

const DropdownMenu = ({
	user,
	isVisible,
	onToggle,
	onSignOutClick,
	onProfileClick,
}: {
	user: IUser;
	isVisible: boolean;
	onToggle: () => void;
	onSignOutClick: () => void;
	onProfileClick: () => void;
}) => (
	<div className="relative flex-shrink-0">
		<div className="flex items-center gap-2 md:gap-4 pr-2 md:pr-3 py-1">
			<Text className="text-primary font-semibold text-sm md:text-base break-words max-w-[120px] md:max-w-[180px]">
				{user.name}
			</Text>
			<SVGIcon
				className="h-1.5 md:h-2 w-auto cursor-pointer select-none fill-primary-light flex-shrink-0"
				icon="Arrow"
				onClick={onToggle}
			/>
		</div>
		<ul
			className={classNames(
				'absolute right-0 mt-2 w-40 md:w-48 bg-white border border-gray-border rounded-md shadow-sm overflow-hidden z-10',
				{ hidden: !isVisible }
			)}
		>
			<li
				className="flex items-center gap-2 md:gap-3 p-1.5 md:p-2 hover:bg-primary-lighter transition-colors cursor-pointer"
				onClick={onProfileClick}
			>
				<SVGIcon
					className="w-3 md:w-4 h-auto fill-primary flex-shrink-0"
					icon="Profile"
				/>
				<Text className="text-primary font-semibold text-sm md:text-base break-words">
					{t('home.profile')}
				</Text>
			</li>
			<li
				className="flex items-center gap-2 md:gap-3 p-1.5 md:p-2 hover:bg-primary-lighter transition-colors cursor-pointer"
				onClick={onSignOutClick}
			>
				<SVGIcon
					className="w-3 md:w-4 h-auto fill-primary flex-shrink-0"
					icon="SignOut"
				/>
				<Text className="text-primary font-semibold text-sm md:text-base break-words">
					{t('login.sign_out')}
				</Text>
			</li>
		</ul>
	</div>
);

const Header = ({
	onSidebarIconClick,
	idClienteSelecionado,
	onClienteChange,
}: HeaderProps) => {
	const { user } = useLoaderData() as HomeLoaderData;
	const location = useLocation();
	const navigate = useNavigate();

	const [isMenuVisible, setIsMenuVisible] = useState(false);
	const [clientes, setClientes] = useState<IClient[]>([]);
	const clienteSelecionado = clientes.find(
		(cliente) => String(cliente.id) === idClienteSelecionado
	);

	useEffect(() => {
		if (!user.permissions.isSystemAdmin) return;
		let ativo = true;
		const carregarClientes = async () => {
			try {
				const resposta = await ClientService.getAll({ isActive: 1 });
				if (!ativo) return;
				if (!resposta?.data) {
					NotificationToast.Warn(t('errors.fetch_clients'));
					return;
				}
				setClientes(resposta.data);
			} catch (erro) {
				if (ativo) NotificationToast.Warn(t('errors.fetch_clients'));
			}
		};
		carregarClientes();
		return () => {
			ativo = false;
		};
	}, [user.permissions.isSystemAdmin]);

	const selecionarCliente = (evento: EditableChangeEvent) => {
		if (!user.permissions.isSystemAdmin) return;
		const cliente = clientes.find(
			(cliente) => cliente.fantasyName === evento.target.value
		);
		if (cliente?.id) onClienteChange(String(cliente.id));
	};

	const toggleMenuVisibility = () => setIsMenuVisible((prev) => !prev);

	const onSignOut = () => {
		AuthService.logout(navigate);
	};

	const onProfileClick = () => {
		navigate('/profile');
		setIsMenuVisible(false);
	};

	return (
		<header className="flex flex-col sm:flex-row flex-wrap items-center gap-3 sm:gap-2 md:gap-4 py-3 md:py-4 px-3 md:px-4 bg-white rounded-b-lg shadow-md">
			{/* Primeira linha: Ícone do menu e logo (sempre visíveis) */}
			<div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-start">
				<div className="flex items-center gap-3">
					<SVGIcon
						className="w-5 h-4 md:w-6 md:h-5 cursor-pointer flex-shrink-0"
						icon="Menu"
						onClick={onSidebarIconClick}
					/>

					<Link to="/" className="flex-shrink-0">
						<SVGIcon
							className="w-auto h-6 md:h-7 lg:h-8"
							icon="LogoColored"
						/>
					</Link>
				</div>

				{/* Language Selector visível em mobile na primeira linha */}
				<div className="block sm:hidden">
					<LanguageSelect className="w-24" />
				</div>
			</div>

			{/* Segunda linha: Language Selector (desktop) e User Menu */}
			<div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:flex-1 sm:justify-end">
				{/* Language Selector - escondido em mobile (já está na primeira linha) */}
				<div className="hidden sm:block">
					<LanguageSelect className="w-32 md:w-40 lg:w-48" />
				</div>

				{user.permissions.isSystemAdmin && location.pathname === '/' && (
					<Select
						className="w-full sm:w-64 text-primary font-bold text-sm"
						name="client"
						placeholder={t('misc.account')}
						value={clienteSelecionado?.fantasyName || ''}
						onChange={selecionarCliente}
						options={clientes.map((cliente) => cliente.fantasyName)}
					/>
				)}

				{/* User Menu */}
				<DropdownMenu
					user={user}
					isVisible={isMenuVisible}
					onToggle={toggleMenuVisibility}
					onSignOutClick={onSignOut}
					onProfileClick={onProfileClick}
				/>
			</div>
		</header>
	);
};

export default Header;
